/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import TwitterHead from "../../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class WomenForWater extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/partners/wfw-women-for-water", market);
    const alt = "Lasting Smiles";
    const title = "women for water · Partners · Lasting Smiles";
    const description = "Give back to women for water with Lasting Smiles organic and all natural lip balm. " +
      "Smile for a cause. " +
      "Give back to charitable causes you care about with sustainable, ethical, and organic skin care products that make a difference all year long. " +
      "The #1 socially responsible and ethical lip care and do good lip balm choice to support your favorite social and environmental non-profit causes caring for people and our planet. " +
      "The perfect gift that gives back.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/partners/wfw-women-for-water", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/partners/wfw-women-for-water", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/purpose", market),
                    "name": "Causes",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/partners/wfw-women-for-water", market),
                    "name": "women for water",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section
          className="bg-yellow-100 px-2 py-10 sm:px-5"
          style={{
            backgroundImage: `url("${require("../../public/images/backgrounds/purpose.jpg")}")` as string,
            backgroundPositionY: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="max-w-screen-lg mx-auto">
            <h2
              className="font-extrabold font-serif sm:text-6xl text-5xl text-center uppercase"
              style={{
                background: "linear-gradient(to bottom right, #FAC551, #F36F48, #F3B411, #F7A4B6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smile for a&nbsp;Cause
            </h2>
            <picture>
              <source
                srcSet={require("../../public/images/logos/wfw-wide.webp") as string}
                type="image/webp"
              />
              <source
                srcSet={require("../../public/images/logos/wfw-wide.png") as string}
                type="image/png"
              />
              <img
                alt="women for water"
                className="block mx-auto"
                src={require("../../public/images/logos/wfw-wide.png") as string}
                style={{ maxHeight: 200 }}
              />
            </picture>
          </div>
        </section>
        <section className="py-10">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif px-2 sm:px-5 text-3xl text-center">
              women for water
            </h1>
            <p className="px-2 sm:px-5 text-xl text-center">
              When women are empowered through water, entire communities are transformed!
              {" "}
              <strong>women for water</strong> helps women and girls to have reliable access to water, sanitation and hygiene which makes them safer, stronger, healthier, educated, employed and heard.
              Our goal is to support the empowerment of women and girls across 10,000 communities worldwide by 2030.
              Currently, we positively impact over 70,000 women and girls in over 300 high need communities across 16 countries.
              With your support, we can increase our efforts to help American communities in need to have clean, affordable and reliable water supplies.
            </p>
            <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:max-w-screen-md sm:mx-auto sm:px-5">
              <div className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white">
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/wfw/photo-1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/wfw/photo-1.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="women for water Photo 1"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/wfw/photo-1.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/wfw/photo-2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/wfw/photo-2.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="women for water Photo 2"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/wfw/photo-2.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/wfw/photo-3.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/wfw/photo-3.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="women for water Photo 3"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/wfw/photo-3.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/wfw/photo-4.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/wfw/photo-4.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="women for water Photo 4"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/wfw/photo-4.jpg") as string}
                    />
                  </picture>
                </div>
              </div>
            </div>
            <p className="px-2 sm:px-5 text-xl text-center">
              When you select <strong>women for water</strong> at checkout, you will be a part of the solution to the many water-related problems the United States faces.
              Today, more than 44 million Americans are served by water systems with quality problems and 2.2 million people have no water at home at all.
              {" "}
              <strong>women for water</strong> addresses this challenge by helping communities and families across the U.S. with water-related challenges.
            </p>
          </div>
        </section>
        <section className="bg-gray-100 px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Meet Beth
            </h2>
            <p className="my-5">
              <picture>
                <source
                  srcSet={require("../../public/images/partners/wfw/photo-beth.webp") as string}
                  type="image/webp"
                />
                <source
                  srcSet={require("../../public/images/partners/wfw/photo-beth.jpg") as string}
                  type="image/jpeg"
                />
                <img
                  alt="Photo of Ariel"
                  className="mx-auto rounded"
                  src={require("../../public/images/partners/wfw/photo-beth.jpg") as string}
                />
              </picture>
            </p>
            <p className="text-center">
              Providing sustainable clean water access to U.S. communities is transforming the lives of so many like Beth, who lived without water in her home in the Navajo Nation for 3 years.
              Beth previously had to collect water from a neighboring town&ndash;hauling heavy buckets of water in order to take a shower, have a drink of water and clean her home.
              With her newly installed water system, Beth spends her newfound time with her grandchildren, sewing, reading, watching the Hallmark channel, and playing oldies music on the local radio station where she's a DJ.
              This impact was made possible by generous contributions from people like you and in collaboration with <strong>women for water</strong> and our implementing partners like DigDeep.
            </p>
            <div className="text-center">
              <Link
                as={toRelativePageURL("/products", market as string)}
                href={{
                  pathname: "/products",
                  query: { market },
                }}
              >
                <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                  Shop &amp; Make a Difference
                </a>
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }
}
